import { useState, useEffect, useContext } from 'react';
import { Grid, Box, Paper, Divider } from '@mui/material';
import { format } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttLabelForm,
    SttFormPeriod,
    SttFormText,
    SttFormSelect,
    SttSelectInterval,
    SttFormTime,
    SelectGroupField,
    SttButtonMonths,
    SttFacilityList,
    SttButtonSave,
    SttButtonDelete,
    SttList,
    SttLabelWeekdays,
    SttButtonCancel,
    SttFullDialog,
} from '../../../sporttia/all';
import {
    getErrorMessage,
    subsetObject,
    updateElementInArray,
} from '../../../lib/utils';
import FreeScheduleSlotDialog from './FreeScheduleSlotDialog';
import { SPORTCENTER_FREE_SCHEDULES } from '../../../lib/queryKeys';
import { useFormatDate } from '../../../lib/hooks';
import DateBuilder from '../../../lib/DateBuilder';
import { PERMISSIONS } from '../../../lib/permissions';

interface FreeScheduleProps {
    idFreeSchedule: number | 'create';
    onClose: () => void;
}

interface Group {
    id: number;
    name: string;
}

interface Facility {
    id: number;
    name: string;
}

interface SlotPrice {
    name: string;
}

type FreeScheduleSlot = {
    id: number;
    ini: string;
    end: string;
    weekdays: number[];
    prices?: SlotPrice[];
};

type FreeScheduleData = {
    id?: number;
    name?: string;
    status: string;
    layer: number;
    months: number[];
    ini: string;
    end: string;
    capacity: number;
    interval: number;
    preBookingIni: string;
    preBookingIniTime: string;
    preBookingEnd: string;
    mandatory: Group | null;
    trash?: boolean;
    created?: string;
    facilities?: Facility[];
    slots?: FreeScheduleSlot[];
} & Record<string, unknown>;

interface FormChange {
    name: string | undefined;
    value: unknown;
}

export default function FreeSchedule({
    idFreeSchedule,
    onClose,
}: FreeScheduleProps) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [creating, setCreating] = useState(false);
    const [title, setTitle] = useState<string | undefined>(undefined);
    const [freeSchedule, setFreeSchedule] = useState<FreeScheduleData>();
    const [facilities, setFacilities] = useState<Facility[]>();
    const [slots, setSlots] = useState<FreeScheduleSlot[]>();
    const [removeSeconds] = useFormatDate();
    const [openedSlotId, setOpenedSlotId] = useState<number | 'create' | null>(
        null,
    );
    const [loading, setLoading] = useState(false);

    /**
     * Load free schedule
     */
    const loadFreeSchedule = () => {
        cxt.api('GET', `/frees/schedules/${idFreeSchedule}`, {
            params: {
                trash: true,
            },
            success: (r: { freeSchedule: FreeScheduleData }) => {
                const schedule = {
                    ...r.freeSchedule,
                    months: r.freeSchedule.months ?? [],
                };
                setFreeSchedule(schedule);
                setFacilities(schedule.facilities);
                setSlots(schedule.slots);
                setTitle(schedule.name);
                setCreating(false);
            },
        });
    };

    /**
     * Save free schedule
     */
    const saveFreeSchedule = () => {
        setLoading(true);

        const params = subsetObject(freeSchedule!, [
            'name',
            'ini',
            'end',
            'status',
            'layer',
            'capacity',
            'interval',
            'preBookingIni',
            'preBookingEnd',
            'preBookingIniTime',
            'months',
        ]) as Record<string, unknown>;

        // Set mandatory group (send 0 when cleared)
        params.idMandatory = freeSchedule?.mandatory?.id ?? 0;

        // Create
        if (creating) {
            cxt.api('POST', `/scs/${cxt.sc!.id}/frees/schedules`, {
                params,
                success: (r: { freeSchedule: FreeScheduleData }) => {
                    cxt.showMessage('S', t('Created'));
                    const schedule = {
                        ...r.freeSchedule,
                        months: r.freeSchedule.months ?? [],
                    };
                    setFreeSchedule(schedule);
                    setFacilities(schedule.facilities);
                    setSlots(schedule.slots);
                    setTitle(schedule.name);
                    setCreating(false);
                    setLoading(false);
                    queryClient.invalidateQueries({
                        queryKey: [SPORTCENTER_FREE_SCHEDULES],
                    });
                },
                error: (err: any) => {
                    cxt.showMessage('E', getErrorMessage(err));
                    setLoading(false);
                },
            });

            // Update
        } else {
            cxt.api('PUT', `/frees/schedules/${freeSchedule!.id}`, {
                params,
                success: () => {
                    cxt.showMessage('S', t('Saved'));
                    if (title !== freeSchedule?.name) {
                        setTitle(freeSchedule?.name);
                    }
                    setLoading(false);
                },
                error: (err: any) => {
                    cxt.showMessage('E', getErrorMessage(err));
                    setLoading(false);
                },
            });
        }
    };

    /**
     * Delete free schedule
     */
    const delFreeSchedule = () => {
        cxt.api('DELETE', `/frees/schedules/${freeSchedule!.id}`, {
            confirmation: true,
            success: () => {
                cxt.showMessage('S', t('Deleted'));
                queryClient.invalidateQueries({
                    queryKey: [SPORTCENTER_FREE_SCHEDULES],
                });
                onClose();
            },
        });
    };

    /**
     * Handle form
     */
    const handleForm = ({ name, value }: FormChange) => {
        if (!name) return;
        let processedValue = value;
        if (name === 'capacity' && value === '') processedValue = 0;
        setFreeSchedule({ ...freeSchedule!, [name]: processedValue });
    };

    /**
     * On month updated
     */
    const onMonthUpdated = (month: number) => {
        if (!freeSchedule) return;

        // Set it off
        if (freeSchedule.months.includes(month)) {
            const newMonths = [...freeSchedule.months];
            newMonths.splice(
                newMonths.findIndex((e) => e === month),
                1,
            );
            setFreeSchedule({ ...freeSchedule, months: newMonths });

            // Set it on
        } else {
            setFreeSchedule({
                ...freeSchedule,
                months: freeSchedule.months.concat([month]),
            });
        }
    };

    /**
     * Add facility
     */
    const addFacility = (facility: Facility) => {
        cxt.api(
            'POST',
            `/frees/schedules/${freeSchedule!.id}/facilities/${facility.id}`,
            {
                confirmation: true,
                success: () => {
                    const newFacilities = [...(facilities || [])];
                    newFacilities.push(facility);
                    setFacilities(newFacilities);
                    cxt.showMessage('S', t('OperationSuccessful'));
                },
            },
        );
    };

    /**
     * Delete facility
     */
    const delFacility = (facility: Facility) => {
        cxt.api(
            'DELETE',
            `/frees/schedules/${freeSchedule!.id}/facilities/${facility.id}`,
            {
                confirmation: true,
                success: () => {
                    const newFacilities = [...(facilities || [])];
                    newFacilities.splice(
                        newFacilities.findIndex((e) => e.id === facility.id),
                        1,
                    );
                    setFacilities(newFacilities);
                },
            },
        );
    };

    /**
     * Open adding slot
     */
    const openAddingSlot = () => {
        setOpenedSlotId('create');
    };

    /**
     * Delete slot
     */
    const delSlot = (slot: FreeScheduleSlot) => {
        cxt.api('DELETE', `/frees/schedules/slots/${slot.id}`, {
            confirmation: true,
            success: () => {
                const newSlots = [...(slots || [])];
                newSlots.splice(
                    newSlots.findIndex((e) => e.id === slot.id),
                    1,
                );
                setSlots(newSlots);
            },
        });
    };

    /**
     * Init
     */
    useEffect(() => {
        if (idFreeSchedule) {
            if (idFreeSchedule === 'create') {
                // Init data to create
                setFreeSchedule({
                    status: 'ACTIVE',
                    layer: 0,
                    months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
                    ini: `${format(new Date(), 'yyyy')}-01-01`,
                    end: `${format(new Date(), 'yyyy')}-12-31`,
                    capacity: 0,
                    interval: 60,
                    preBookingIni: '0000-00-00',
                    preBookingIniTime: '00:00',
                    preBookingEnd: '0000-00-00T00:00:00',
                    mandatory: null,
                });

                setTitle(t('New'));
                // Set status and header
                setCreating(true);
            } else {
                loadFreeSchedule();
            }
        }
    }, [idFreeSchedule]);

    // -----| Render |-----
    return (
        <SttFullDialog
            open
            title={title}
            onClose={onClose}
            urlPath={
                idFreeSchedule !== 'create'
                    ? `/frees/schedules/${idFreeSchedule}`
                    : undefined
            }
        >
            <Box className="p-6">
                <Grid container spacing={3}>
                    {/* Left side - Detail controls */}
                    <Grid
                        size={{
                            xs: 12,
                            md:
                                freeSchedule && !freeSchedule.trash && !creating
                                    ? 6
                                    : 12,
                        }}
                    >
                        <Paper>
                            <Box className="p-6">
                                {freeSchedule && (
                                    <Grid container spacing={3}>
                                        {/* Basic Information */}
                                        {!creating && (
                                            <Grid size={12}>
                                                <SttLabelForm
                                                    title={t('Created')}
                                                    text={new DateBuilder(
                                                        freeSchedule.created,
                                                        cxt?.sc?.timezone?.name,
                                                        cxt?.lang?.key,
                                                    ).dmyhm()}
                                                />
                                            </Grid>
                                        )}

                                        <SttFormText
                                            grid
                                            md={9}
                                            sm={8}
                                            xs={12}
                                            name="name"
                                            caption={t('Name')}
                                            defVal={freeSchedule.name}
                                            onChange={handleForm}
                                        />

                                        <SttFormSelect
                                            grid
                                            md={3}
                                            sm={4}
                                            xs={12}
                                            name="status"
                                            caption={t('Status')}
                                            defVal={freeSchedule.status}
                                            onChange={handleForm}
                                            options={
                                                cxt.constants.scheduleStatuses
                                            }
                                        />

                                        <Grid size={12}>
                                            <Divider />
                                        </Grid>

                                        {/* Period & Months */}
                                        <SttFormPeriod
                                            grid
                                            md={12}
                                            sm={12}
                                            xs={12}
                                            caption={t('Period')}
                                            nameIni="ini"
                                            nameEnd="end"
                                            defValIni={freeSchedule.ini}
                                            defValEnd={freeSchedule.end}
                                            onChange={handleForm}
                                        />

                                        <Grid size={12}>
                                            <SttButtonMonths
                                                tinyLabels
                                                selectedMonths={
                                                    freeSchedule.months
                                                }
                                                onChangeMonth={onMonthUpdated}
                                            />
                                        </Grid>

                                        <Grid size={12}>
                                            <Divider />
                                        </Grid>

                                        {/* Configuration */}
                                        <SttFormSelect
                                            grid
                                            md={4}
                                            sm={4}
                                            xs={12}
                                            name="layer"
                                            caption={t('Layer')}
                                            defVal={freeSchedule.layer}
                                            onChange={handleForm}
                                            options={cxt.constants.freeLayers}
                                        />

                                        <SttFormText
                                            grid
                                            md={4}
                                            sm={4}
                                            xs={6}
                                            name="capacity"
                                            caption={t('Capacity')}
                                            defVal={String(
                                                freeSchedule.capacity,
                                            )}
                                            onChange={handleForm}
                                        />

                                        <SttSelectInterval
                                            grid
                                            md={4}
                                            sm={4}
                                            xs={6}
                                            caption={t('Interval')}
                                            defVal={freeSchedule.interval}
                                            onChange={(minutes: number) =>
                                                handleForm({
                                                    name: 'interval',
                                                    value: minutes,
                                                })
                                            }
                                            min={10}
                                            max={1435}
                                            interval={5}
                                        />

                                        <Grid size={12}>
                                            <Divider />
                                        </Grid>

                                        {/* Booking Settings */}
                                        <SttFormSelect
                                            grid
                                            md={6}
                                            sm={6}
                                            xs={12}
                                            name="preBookingIni"
                                            caption={t('PeriodRenting')}
                                            defVal={freeSchedule.preBookingIni}
                                            onChange={handleForm}
                                            options={
                                                cxt.constants
                                                    .bookingInAdvanceIniIntervals
                                            }
                                        />

                                        <SttFormTime
                                            grid
                                            md={6}
                                            sm={6}
                                            xs={12}
                                            name="preBookingIniTime"
                                            caption={t('Time')}
                                            defVal={
                                                freeSchedule.preBookingIniTime
                                            }
                                            interval={30}
                                            disableClearable={false}
                                            onChange={handleForm}
                                        />

                                        <SttFormSelect
                                            grid
                                            md={6}
                                            sm={6}
                                            xs={12}
                                            name="preBookingEnd"
                                            caption={t('Until')}
                                            defVal={freeSchedule.preBookingEnd}
                                            onChange={handleForm}
                                            options={
                                                cxt.constants
                                                    .bookingInAdvanceEndIntervals
                                            }
                                        />

                                        <SelectGroupField
                                            grid
                                            md={6}
                                            sm={6}
                                            xs={12}
                                            name="mandatory"
                                            caption={t('MandatoryGroup')}
                                            defVal={freeSchedule.mandatory}
                                            onChange={handleForm}
                                        />
                                    </Grid>
                                )}
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Right side - Lists */}
                    {freeSchedule && !freeSchedule.trash && !creating && (
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Grid container spacing={3}>
                                <Grid size={12}>
                                    <Paper>
                                        <SttFacilityList
                                            permission={
                                                PERMISSIONS.SCHEDULE.MODIFY
                                            }
                                            facilities={facilities || []}
                                            onAdd={(facility: Facility) =>
                                                addFacility(facility)
                                            }
                                            onDelete={(facility: Facility) =>
                                                delFacility(facility)
                                            }
                                        />
                                    </Paper>
                                </Grid>

                                <Grid size={12}>
                                    <Paper>
                                        <SttList
                                            title={t('TimeSlots')}
                                            onAdd={() => openAddingSlot()}
                                            onDelete={delSlot}
                                            onSelect={(
                                                slot: FreeScheduleSlot,
                                            ) => setOpenedSlotId(slot.id)}
                                            data={
                                                slots &&
                                                slots.map(
                                                    (
                                                        slot: FreeScheduleSlot,
                                                    ) => ({
                                                        caption: (
                                                            <Box className="flex">
                                                                <SttLabelWeekdays
                                                                    weekdays={
                                                                        slot.weekdays
                                                                    }
                                                                />

                                                                <Box className="ml-4">
                                                                    {removeSeconds(
                                                                        slot.ini,
                                                                    )}{' '}
                                                                    -{' '}
                                                                    {removeSeconds(
                                                                        slot.end,
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        ),
                                                        objToSelect: slot,
                                                        chips:
                                                            slot.prices &&
                                                            slot.prices.map(
                                                                (
                                                                    price: SlotPrice,
                                                                ) => ({
                                                                    caption:
                                                                        price.name,
                                                                }),
                                                            ),
                                                    }),
                                                )
                                            }
                                        />
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}
                </Grid>

                {freeSchedule && (
                    <Box className="mt-6 flex flex-wrap justify-start gap-4">
                        {!creating && !freeSchedule.trash && (
                            <SttButtonDelete
                                onClick={() => delFreeSchedule()}
                                permission={PERMISSIONS.SCHEDULE.DELETE}
                            />
                        )}

                        {!freeSchedule.trash && (
                            <SttButtonSave
                                loading={loading}
                                onClick={() => saveFreeSchedule()}
                                permission={
                                    creating
                                        ? PERMISSIONS.SCHEDULE.CREATE
                                        : PERMISSIONS.SCHEDULE.MODIFY
                                }
                            />
                        )}

                        {creating && (
                            <SttButtonCancel
                                onClick={() => {
                                    onClose();
                                }}
                            />
                        )}
                    </Box>
                )}

                <FreeScheduleSlotDialog
                    idSlot={openedSlotId}
                    freeSchedule={
                        freeSchedule ? { id: freeSchedule.id! } : undefined
                    }
                    onUpdate={(slot) => {
                        setSlots(
                            updateElementInArray(
                                slots,
                                slot as FreeScheduleSlot,
                            ),
                        );
                    }}
                    onClose={() => setOpenedSlotId(null)}
                />
            </Box>
        </SttFullDialog>
    );
}
